import { NetworkStatus } from '@apollo/client';
import * as Sentry from '@sentry/nextjs';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { useCallback, useEffect } from 'react';

import { StaticFilter, usePropertyFilters } from '@core/offices/store/filters';
import { LocationAvailableFilters } from '@core/offices/types';
import { OfficeDto, Sortable, SortStatus, useGetOfficesListingLazyQuery } from '@graphql/hooks-and-types';

import { useProperties } from '../../store/office';

import { useGetDynamicFilterValues, useGetSelectedLocationType, useGetStaticFilters } from './filterActions';
import { getAvailableFilters } from './getProperitiesFilters';
import { useGetPropertyPaginator } from './propertiesActions';

export const defaultSort = {
  sort: [
    {
      column: 'id',
      ordering: SortStatus.Asc,
    },
  ],
};

// eslint-disable-next-line no-shadow
export enum SORT_OPTIONS {
  DEFAULT = 'default',
  PRICE_ASC = 'price-asc',
  PRICE_DESC = 'price-desc',
  DATE_ASC = 'date-asc',
  DATE_DESC = 'date-desc',
}

// `labelKey` is the i18n key — translate with t(labelKey) at the rendering component.
// `label` is kept temporarily for consumers that have not migrated to labelKey yet.
export const sortDropDownOptions = [
  {
    label: 'Default',
    labelKey: 'rentalSearch.sortOptions.default',
    value: SORT_OPTIONS.DEFAULT,
  },
  {
    label: 'Price (Low to High)',
    labelKey: 'rentalSearch.sortOptions.priceLowToHigh',
    value: SORT_OPTIONS.PRICE_ASC,
  },
  {
    label: 'Price (High to Low)',
    labelKey: 'rentalSearch.sortOptions.priceHighToLow',
    value: SORT_OPTIONS.PRICE_DESC,
  },
  {
    label: 'Date (Newest to Oldest)',
    labelKey: 'rentalSearch.sortOptions.dateNewestToOldest',
    value: SORT_OPTIONS.DATE_DESC,
  },
  {
    label: 'Date (Oldest to Newest)',
    labelKey: 'rentalSearch.sortOptions.dateOldestToNewest',
    value: SORT_OPTIONS.DATE_ASC,
  },
];

export const UNITS_SORT_OPTIONS = new Map<string, Sortable>([
  [
    'price-asc',
    {
      sort: [
        {
          column: 'baseRate',
          ordering: SortStatus.Asc,
        },
      ],
    },
  ],
  ['default', defaultSort],
  [
    'price-desc',
    {
      sort: [
        {
          column: 'baseRate',
          ordering: SortStatus.Desc,
        },
      ],
    },
  ],
  [
    'date-asc',
    {
      sort: [
        {
          column: 'createdAt',
          ordering: SortStatus.Asc,
        },
      ],
    },
  ],
  [
    'date-desc',
    {
      sort: [
        {
          column: 'createdAt',
          ordering: SortStatus.Desc,
        },
      ],
    },
  ],
]);

export const useRentalForm = () => {
  const router = useRouter();
  const [query, { error, loading, fetchMore: fetchMoreData, networkStatus }] = useGetOfficesListingLazyQuery({
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { setProperties } = useProperties();
  const { setAvailableFilter, setAddress, setLocationType, updateStaticFilters } = usePropertyFilters();

  const locationType = useGetSelectedLocationType();
  const dynamicFilterValues = useGetDynamicFilterValues();
  const staticFilters = useGetStaticFilters();
  const propertyPaginatorInfo = useGetPropertyPaginator();

  const formatDate = (date: dayjs.Dayjs) => {
    return date.format('YYYY-MM-DD');
  };

  const formatStaticData = (staticFilter: StaticFilter) => ({
    address: staticFilter.address,
    propertyType: staticFilter.selectedPropertyTypes.length ? staticFilter.selectedPropertyTypes : null,
    officeType: staticFilter.unitType ? [`${staticFilter.unitType || ''}`] : null,
    amenitiesIds: staticFilter.selectedAmenities?.length
      ? staticFilter.selectedAmenities.map((amenity) => parseInt(amenity, 10))
      : null,
    price: staticFilter.priceRange
      ? staticFilter.priceRange.split('-').map((value) => parseInt(value, 10))
      : [0, 1000000],
    moveIn: staticFilter.moveInDate ? formatDate(staticFilter.moveInDate) : null,
    moveOut: null,
  });

  const fetchMore = useCallback(async () => {
    if (propertyPaginatorInfo.hasMorePages) {
      const { data } = await fetchMoreData({
        variables: {
          paginate: {
            per_page: 20,
            page: propertyPaginatorInfo.currentPage + 1,
          },
        },
      });
      if (data) {
        setProperties(data.getOfficesListing.data as OfficeDto[], data.getOfficesListing.paginatorInfo);
      }
    }
  }, [fetchMoreData, propertyPaginatorInfo]);

  const handleSubmit = useCallback(
    async (staticFilter: StaticFilter) => {
      updateStaticFilters(staticFilter);
      try {
        const { data } = await query({
          variables: {
            data: {
              staticFilter: formatStaticData(staticFilter),
              dynamicFilter: Object.values(dynamicFilterValues).map((filter) => ({
                ...filter,
                value: `${filter.value}` || '',
                id: parseInt(filter.id as unknown as string, 10),
              })),
            },
            sort: UNITS_SORT_OPTIONS.get(staticFilter.sort),
            paginate: {
              per_page: 20,
              page: 1,
            },
          },
          context: {
            url: `https://${router.query?.clientName}.pickspace.com/graphql/`,
          },
        });
        if (data) {
          setProperties(data.getOfficesListing.data as OfficeDto[], data.getOfficesListing.paginatorInfo);
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [query, router.query?.clientName, dynamicFilterValues, updateStaticFilters],
  );

  useEffect(() => {
    const { address, locationType } = router.query;
    if (address) {
      setAddress(address as string);
    }
    if (locationType) {
      setLocationType(locationType as string);
    }
  }, []);

  useEffect(() => {
    if (locationType && router.query?.clientName) {
      getAvailableFilters(router.query.clientName as string, locationType).then(
        (serverAvailableFilters: LocationAvailableFilters[]) => {
          setAvailableFilter(serverAvailableFilters);
        },
      );
    }
  }, [locationType, router?.query.clientName]);

  useEffect(() => {
    if (staticFilters.unitType) handleSubmit(staticFilters);
  }, [staticFilters.unitType]);

  return {
    error,
    loading,
    isRefetching: networkStatus === NetworkStatus.fetchMore,
    handleSubmit,
    fetchMore,
  };
};
